import { apiFetch, apiDownloadNamed, type IdResponse } from "@/lib/apiClient";
import { fetchAll, pageQuery, toPaged, type Paged, type PageParams } from "@/lib/paging";
import { toRoleTier, type UserRoleTier } from "@/features/auth/api/auth.api";

interface UserListResponse {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  userNumber: number | null;
  jobTitle: string | null;
  branch: string | null;
  group: string | null;
  assignedRoleId: string | null;
  creationTime: string;
  username: string | null;
  lastLoginAt: string | null;
  assignedRoleName: string | null;
  managerName: string | null;
}

interface UserDetailResponse extends UserListResponse {
  managerId: string | null;
  startDate: string | null;
  notifyOnTicketAssign: boolean;
  phone: string | null;
  dateOfBirth: string | null;
  hasLicense: boolean;
  hasVoidCheck: boolean;
  subAgents: { id: string; name: string; email: string | null; userNumber: number | null; status: string }[] | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  irsReportingName?: string | null;
  driverLicenseName?: string | null;
  voidCheckName?: string | null;
  ssn?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  legalName?: string | null;
  ownershipType?: string | null;
  ein?: string | null;
  businessPhone?: string | null;
  businessEmail?: string | null;
  bankRoutingNumber?: string | null;
  bankAccountNumber?: string | null;
  businessAddressLine1?: string | null;
  businessCity?: string | null;
  businessState?: string | null;
  businessZip?: string | null;
}

function toUserListItem(r: UserListResponse): UserListItem {
  return {
    id: r.id,
    userNumber: r.userNumber ?? null,
    email: r.email,
    username: r.username ?? null,
    name: r.name,
    role: toRoleTier(r.role),
    status: r.status,
    assignedRoleId: r.assignedRoleId ?? null,
    assignedRoleName: r.assignedRoleName ?? null,
    managerId: null,
    managerName: r.managerName ?? null,
    lastLoginAt: r.lastLoginAt ?? null,
  };
}

function toUserDetail(r: UserDetailResponse): UserDetail {
  return {
    id: r.id,
    userNumber: r.userNumber ?? null,
    email: r.email,
    username: r.username ?? null,
    name: r.name,
    role: toRoleTier(r.role),
    status: r.status,
    assignedRoleId: r.assignedRoleId ?? null,
    assignedRoleName: r.assignedRoleName ?? null,
    managerId: r.managerId ?? null,
    managerName: r.managerName ?? null,
    createdAt: r.creationTime,
    updatedAt: r.creationTime,
    lastLoginAt: r.lastLoginAt ?? null,
    lastLoginIp: null,
    notifyOnTicketAssign: r.notifyOnTicketAssign ?? false,
    startDate: r.startDate ?? null,
    jobTitle: r.jobTitle ?? null,
    branch: r.branch ?? null,
    group: r.group ?? null,
    hasLicense: r.hasLicense ?? false,
    hasVoidCheck: r.hasVoidCheck ?? false,
    phone: r.phone ?? null,
    dateOfBirth: r.dateOfBirth ?? null,
    firstName: r.firstName ?? null,
    middleName: r.middleName ?? null,
    lastName: r.lastName ?? null,
    irsReportingName: r.irsReportingName ?? null,
    driverLicenseName: r.driverLicenseName ?? null,
    voidCheckName: r.voidCheckName ?? null,
    ssn: r.ssn ?? null,
    addressLine1: r.addressLine1 ?? null,
    city: r.city ?? null,
    state: r.state ?? null,
    zip: r.zip ?? null,
    legalName: r.legalName ?? null,
    ownershipType: r.ownershipType ?? null,
    ein: r.ein ?? null,
    businessPhone: r.businessPhone ?? null,
    businessEmail: r.businessEmail ?? null,
    bankRoutingNumber: r.bankRoutingNumber ?? null,
    bankAccountNumber: r.bankAccountNumber ?? null,
    businessAddressLine1: r.businessAddressLine1 ?? null,
    businessCity: r.businessCity ?? null,
    businessState: r.businessState ?? null,
    businessZip: r.businessZip ?? null,
    subAgents: (r.subAgents ?? []).map((s) => ({
      id: s.id,
      userNumber: s.userNumber ?? null,
      name: s.name,
      email: s.email ?? null,
      status: s.status,
    })),
  };
}

export interface UserListItem {
  id: string;
  userNumber: number | null;
  email: string;
  username: string | null;
  name: string;
  role: UserRoleTier;
  status: string;
  assignedRoleId: string | null;
  assignedRoleName: string | null;
  managerId: string | null;
  managerName: string | null;
  lastLoginAt: string | null;
}

export interface MyActivityRow {
  occurredAt: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  detail: string | null;
}

export interface UserDetail {
  id: string;
  userNumber: number | null;
  email: string;
  username: string | null;
  name: string;
  role: UserRoleTier;
  status: string;
  assignedRoleId: string | null;
  assignedRoleName: string | null;
  managerId: string | null;
  managerName: string | null;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  lastLoginIp: string | null;

  notifyOnTicketAssign: boolean;
  startDate: string | null;
  jobTitle: string | null;
  branch: string | null;
  group: string | null;
  hasLicense: boolean;
  hasVoidCheck: boolean;
  subAgents: SubAgentItem[];

  phone: string | null;
  dateOfBirth: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  irsReportingName: string | null;
  driverLicenseName: string | null;
  voidCheckName: string | null;
  ssn: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;

  legalName: string | null;
  ownershipType: string | null;
  ein: string | null;
  businessPhone: string | null;
  businessEmail: string | null;
  bankRoutingNumber: string | null;
  bankAccountNumber: string | null;
  businessAddressLine1: string | null;
  businessCity: string | null;
  businessState: string | null;
  businessZip: string | null;
}

export interface SubAgentItem {
  id: string;
  userNumber: number | null;
  name: string;
  email: string | null;
  status: string;
}

export interface ClearableUserFields {
  dateOfBirth?: string | null;
  phone?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  irsReportingName?: string | null;
  ssn?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  legalName?: string | null;
  ownershipType?: string | null;
  ein?: string | null;
  businessPhone?: string | null;
  businessEmail?: string | null;
  bankRoutingNumber?: string | null;
  bankAccountNumber?: string | null;
  businessAddressLine1?: string | null;
  businessCity?: string | null;
  businessState?: string | null;
  businessZip?: string | null;
}

export interface CreateUserRequest extends ClearableUserFields {
  email: string;
  name: string;
  password: string;
  username?: string | null;
  role?: UserRoleTier;
  status?: "Active" | "Inactive";
  userNumber?: number | null;
  assignedRoleId?: string | null;
  managerId?: string | null;
  startDate?: string | null;
  jobTitle?: string | null;
  branch?: string | null;
  group?: string | null;
}

export interface UpdateUserRequest extends ClearableUserFields {
  email?: string;
  name?: string;

  password?: string;
  username?: string | null;
  role?: UserRoleTier;
  status?: "Active" | "Inactive";
  userNumber?: number | null;
  assignedRoleId?: string | null;
  managerId?: string | null;
  startDate?: string | null;
  jobTitle?: string | null;
  branch?: string | null;
  group?: string | null;
}

export interface RoleSummary {
  id: string;
  name: string;
  description: string | null;
  permissionCount: number;
  userCount: number;
  isSystem: boolean;
}

export interface RoleGrant {
  permissionId: string;
  canUse: boolean;
  canReassign: boolean;
}

export interface RoleDetail {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
  grants: RoleGrant[];
}

export interface CreateRoleRequest {
  name: string;
  description?: string | null;
  grants?: RoleGrant[];
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string | null;
}

export type PermissionKind = "access" | "display" | "preference" | "eligibility";

export interface PermissionCatalogEntry {
  id: string;
  code: number;
  key: string;
  name: string;
  category: string;
  description: string | null;
  parentCode: number | null;
  displayOnly: boolean;
  disabled: boolean;
  kind?: PermissionKind;
}

export type UserDeletionGroup =
  | "team"
  | "merchants"
  | "applications"
  | "tickets"
  | "sales"
  | "partner"
  | "history"
  | "credentials";

export interface UserDeletionItem {
  key: string;
  group: UserDeletionGroup;
  label: string;
  count: number;
  activeCount: number | null;
}

export interface UserDeletionSummary {
  userId: string;
  name: string;
  email: string | null;
  userNumber: number | null;
  role: string;
  status: string;
  canDelete: boolean;
  blockedReason: string | null;
  requiresReassignment: boolean;
  canDeleteDirectly: boolean;
  totalToTransfer: number;
  merchants: { total: number; owned: number; ownedLive: number; associate: number; assistant: number };
  tickets: { assignedOpen: number; assignedClosed: number; assignedTotal: number; created: number; comments: number };
  transferItems: UserDeletionItem[];
  removedItems: UserDeletionItem[];
  suggestedReassignTo: { id: string; name: string; email: string | null; reason: string } | null;
}

export interface UserDeleteResult {
  deletedUserId: string;
  reassignedToUserId: string | null;
  rowsReassigned: number;
  credentialsRevoked: number;
  partyPurged: boolean;
  attachmentsRemoved: number;
  details: Record<string, number>;
}

type Numeric = number | string | null | undefined;

function num(v: Numeric): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function numOrNull(v: Numeric): number | null {
  return v == null || v === "" ? null : num(v);
}

type Wire<T> = { [K in keyof T]: T[K] extends number ? Numeric : T[K] extends number | null ? Numeric : T[K] };

interface UserDeletionSummaryResponse extends Omit<Wire<UserDeletionSummary>, "merchants" | "tickets" | "transferItems" | "removedItems"> {
  merchants: Wire<UserDeletionSummary["merchants"]> | null;
  tickets: Wire<UserDeletionSummary["tickets"]> | null;
  transferItems: Wire<UserDeletionItem>[] | null;
  removedItems: Wire<UserDeletionItem>[] | null;
}

function toDeletionItem(r: Wire<UserDeletionItem>): UserDeletionItem {
  return { key: r.key, group: r.group, label: r.label, count: num(r.count), activeCount: numOrNull(r.activeCount) };
}

function toDeletionSummary(r: UserDeletionSummaryResponse): UserDeletionSummary {
  const m = r.merchants;
  const t = r.tickets;
  return {
    userId: r.userId,
    name: r.name,
    email: r.email ?? null,
    userNumber: numOrNull(r.userNumber),
    role: r.role,
    status: r.status,
    canDelete: r.canDelete,
    blockedReason: r.blockedReason ?? null,
    requiresReassignment: r.requiresReassignment,
    canDeleteDirectly: r.canDeleteDirectly,
    totalToTransfer: num(r.totalToTransfer),
    merchants: {
      total: num(m?.total),
      owned: num(m?.owned),
      ownedLive: num(m?.ownedLive),
      associate: num(m?.associate),
      assistant: num(m?.assistant),
    },
    tickets: {
      assignedOpen: num(t?.assignedOpen),
      assignedClosed: num(t?.assignedClosed),
      assignedTotal: num(t?.assignedTotal),
      created: num(t?.created),
      comments: num(t?.comments),
    },
    transferItems: (r.transferItems ?? []).map(toDeletionItem),
    removedItems: (r.removedItems ?? []).map(toDeletionItem),
    suggestedReassignTo: r.suggestedReassignTo ?? null,
  };
}

const USER_SEARCH_COLS = ["name", "email", "username"];

function clearableBody(req: ClearableUserFields) {
  return {
    dateOfBirth: req.dateOfBirth ?? null,
    phone: req.phone ?? null,
    firstName: req.firstName ?? null,
    middleName: req.middleName ?? null,
    lastName: req.lastName ?? null,
    irsReportingName: req.irsReportingName ?? null,
    ssn: req.ssn ?? null,
    addressLine1: req.addressLine1 ?? null,
    city: req.city ?? null,
    state: req.state ?? null,
    zip: req.zip ?? null,
    legalName: req.legalName ?? null,
    ownershipType: req.ownershipType ?? null,
    ein: req.ein ?? null,
    businessPhone: req.businessPhone ?? null,
    businessEmail: req.businessEmail ?? null,
    bankRoutingNumber: req.bankRoutingNumber ?? null,
    bankAccountNumber: req.bankAccountNumber ?? null,
    businessAddressLine1: req.businessAddressLine1 ?? null,
    businessCity: req.businessCity ?? null,
    businessState: req.businessState ?? null,
    businessZip: req.businessZip ?? null,
  };
}

export const users = {
  async list(params: PageParams = {}): Promise<Paged<UserListItem>> {
    const res = await apiFetch<Paged<UserListResponse> | UserListResponse[]>(
      `/users${pageQuery(params, undefined, USER_SEARCH_COLS)}`,
    );
    const p = toPaged<UserListResponse>(res);
    return { ...p, items: p.items.map(toUserListItem) };
  },
  listAll(keyword?: string): Promise<UserListItem[]> {
    return fetchAll((p) => this.list(p), { keyword }, "users");
  },
  async get(id: string) {
    const r = await apiFetch<UserDetailResponse>(`/users/${encodeURIComponent(id)}`);
    return toUserDetail(r);
  },

  async getMe() {
    const r = await apiFetch<UserDetailResponse>("/users/me");
    return toUserDetail(r);
  },

  myActivity(limit?: number) {
    const q = limit ? `?limit=${encodeURIComponent(String(limit))}` : "";
    return apiFetch<MyActivityRow[]>(`/users/me/activity${q}`);
  },
  create(req: CreateUserRequest) {
    return apiFetch<IdResponse>("/users", {
      method: "POST",
      body: JSON.stringify({
        email: req.email,
        name: req.name,
        password: req.password,
        role: req.role ?? "USER",
        assignedRoleId: req.assignedRoleId ?? null,
        username: req.username ?? null,
        jobTitle: req.jobTitle ?? null,
        branch: req.branch ?? null,
        group: req.group ?? null,
        startDate: req.startDate ?? null,
        managerId: req.managerId ?? null,
        userNumber: req.userNumber ?? null,
        ...clearableBody(req),
      }),
    });
  },
  update(id: string, req: UpdateUserRequest) {
    return apiFetch<void>(`/users/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify({
        email: req.email,
        name: req.name,
        username: req.username,
        role: req.role,
        jobTitle: req.jobTitle,
        branch: req.branch,
        group: req.group,
        startDate: req.startDate,
        managerId: req.managerId,
        assignedRoleId: req.assignedRoleId,
        status: req.status,
        userNumber: req.userNumber,
        ...clearableBody(req),
      }),
    });
  },
  async deletionSummary(id: string) {
    const r = await apiFetch<UserDeletionSummaryResponse>(`/users/${encodeURIComponent(id)}/deletion-summary`);
    return toDeletionSummary(r);
  },
  async remove(id: string, reassignToUserId?: string): Promise<UserDeleteResult | null> {
    const q = reassignToUserId ? `?reassignTo=${encodeURIComponent(reassignToUserId)}` : "";
    const r = await apiFetch<Wire<UserDeleteResult> | undefined>(`/users/${encodeURIComponent(id)}${q}`, { method: "DELETE" });
    if (!r) return null;
    return {
      deletedUserId: r.deletedUserId,
      reassignedToUserId: r.reassignedToUserId ?? null,
      rowsReassigned: num(r.rowsReassigned),
      credentialsRevoked: num(r.credentialsRevoked),
      partyPurged: r.partyPurged,
      attachmentsRemoved: num(r.attachmentsRemoved),
      details: Object.fromEntries(Object.entries(r.details ?? {}).map(([k, v]) => [k, num(v as Numeric)])),
    };
  },
  resetPassword(id: string, newPassword: string) {
    return apiFetch<void>(`/users/${encodeURIComponent(id)}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ newPassword }),
    });
  },

  documentFile(id: string, which: "license" | "check") {
    return apiDownloadNamed(`/users/${encodeURIComponent(id)}/documents/${which}`);
  },

  sendResetEmail(id: string) {
    return apiFetch<{ sent: boolean; to: string }>(
      `/users/${encodeURIComponent(id)}/send-reset-email`,
      { method: "POST" },
    );
  },

  changeMyPassword(currentPassword: string, newPassword: string) {
    return apiFetch<void>("/users/me/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  updateMyNotificationPrefs(notifyOnTicketAssign: boolean) {
    return apiFetch<void>("/users/me/notification-prefs", {
      method: "POST",
      body: JSON.stringify({ notifyOnTicketAssign }),
    });
  },
};

interface RoleListResponse {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  userCount?: number | null;
  permissionCount: number;
}

interface RolePermissionItem {
  permissionId: string;
  canUse: boolean;
  canReassign: boolean;
}

interface RoleDetailResponse {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  userCount?: number | null;
  permissions: RolePermissionItem[];
}

export const roles = {
  async list() {
    const res = await apiFetch<Paged<RoleListResponse> | RoleListResponse[]>("/roles");
    return toPaged<RoleListResponse>(res).items.map(
      (r): RoleSummary => ({
        id: r.id,
        name: r.name,
        description: r.description ?? null,
        permissionCount: r.permissionCount ?? 0,
        userCount: r.userCount ?? 0,
        isSystem: r.isSystem,
      }),
    );
  },
  async get(id: string) {
    const r = await apiFetch<RoleDetailResponse>(`/roles/${encodeURIComponent(id)}`);
    return {
      id: r.id,
      name: r.name,
      description: r.description ?? null,
      isSystem: r.isSystem,
      userCount: r.userCount ?? 0,
      createdAt: "",
      updatedAt: "",
      grants: (r.permissions ?? []).map((p) => ({
        permissionId: p.permissionId,
        canUse: p.canUse,
        canReassign: p.canReassign,
      })),
    } satisfies RoleDetail;
  },
  async create(req: CreateRoleRequest): Promise<{ id: string }> {
    const res = await apiFetch<IdResponse>("/roles", {
      method: "POST",
      body: JSON.stringify({ name: req.name, description: req.description ?? null }),
    });
    return { id: res.id };
  },
  update(id: string, req: UpdateRoleRequest) {
    return apiFetch<void>(`/roles/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify({ name: req.name, description: req.description ?? null }),
    });
  },
  setGrants(id: string, grants: RoleGrant[]) {
    return apiFetch<void>(`/roles/${encodeURIComponent(id)}/permissions`, {
      method: "PUT",
      body: JSON.stringify({ permissions: grants }),
    });
  },
  remove(id: string) {
    return apiFetch<void>(`/roles/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

export const permissions = {
  catalog() {
    return apiFetch<PermissionCatalogEntry[]>("/permissions/catalog");
  },
};
